// src/components/AddDeploymentForm.js
import React, { useEffect, useState, useRef } from "react";
import "./AddDeploymentForm.css";

const DEFAULT_COLLECTION_LOCATION_ID = 1;

export default function AddDeploymentForm({ onClose, onCreated, userId }) {
  const [step, setStep] = useState(1);

  const [locations, setLocations] = useState([]);
  const [deploymentTypes, setDeploymentTypes] = useState([]);
  const [inventory, setInventory] = useState([]);          // full list (for DEPLOY)
  const [locationInventory, setLocationInventory] = useState([]); // by deployment location
  const [locationSearch, setLocationSearch] = useState("");
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const locationWrapperRef = useRef(null);

  const [form, setForm] = useState({
    deploymenttype_id: "",
    vendor_id: "",
    deployment_location_id: "",
    transfer_location_id: "",
    need_approval: false,
  });

  const [deploySelected, setDeploySelected] = useState([]);
  const [collectSelected, setCollectSelected] = useState([]);
  const [transferSelected, setTransferSelected] = useState([]);

  const [deploySearch, setDeploySearch] = useState("");
  const [collectSearch, setCollectSearch] = useState("");
  const [transferSearch, setTransferSearch] = useState("");

  const [collectIncludePO, setCollectIncludePO] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // ---------- Load static data on mount ----------
  useEffect(() => {
    fetch("/api/locations")
      .then((r) => r.json())
      .then((rows) => setLocations(Array.isArray(rows) ? rows : []))
      .catch((err) => {
        console.error("Error fetching locations", err);
        setError("Failed to load locations.");
      });

    fetch("/api/deployment-types")
      .then((r) => r.json())
      .then((rows) => setDeploymentTypes(Array.isArray(rows) ? rows : []))
      .catch((err) => {
        console.error("Error fetching deployment types", err);
        setError("Failed to load deployment types.");
      });

    fetch("/api/products?all=true")
      .then((r) => r.json())
      .then((data) => {
        const rows = Array.isArray(data) ? data : (data.items || []);
        setInventory(rows);
      })
      .catch((err) => {
        console.error("Error fetching inventory", err);
        setError("Failed to load inventory list.");
      });
  }, []);

  // ---------- Load inventory for selected deployment location ----------
  useEffect(() => {
    const locId = form.deployment_location_id;
    if (!locId) {
      setLocationInventory([]);
      setCollectSelected([]);
      setTransferSelected([]);
      return;
    }

    fetch(`/api/products/by-location/${locId}`)
      .then((r) => r.json())
      .then((rows) => {
        const list = Array.isArray(rows) ? rows : [];
        setLocationInventory(list);

        // reset COLLECT / TRANSFER selections when location changes
        setCollectSelected([]);
        setTransferSelected([]);
      })
      .catch((err) => {
        console.error("Error fetching products by location", err);
        setError("Failed to load inventory for selected location.");
      });
  }, [form.deployment_location_id]);

  // ---------- Close location dropdown when clicking outside ----------
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        locationWrapperRef.current &&
        !locationWrapperRef.current.contains(e.target)
      ) {
        setShowLocationDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ---------- Helpers ----------
  const updateForm = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const toggleInArray = (arr, id) =>
    arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id];

  const toggleDeploy = (id) =>
    setDeploySelected((prev) => toggleInArray(prev, id));
  const toggleCollect = (id) =>
    setCollectSelected((prev) => toggleInArray(prev, id));
  const toggleTransfer = (id) =>
    setTransferSelected((prev) => toggleInArray(prev, id));

  const filterBySearch = (list, query) => {
    if (!query.trim()) return list;
    const q = query.toLowerCase();
    return list.filter((i) => {
      return (
        (i.asset1_name || i.asset1_id || "")
          .toString()
          .toLowerCase()
          .includes(q) ||
        (i.imei_number || "").toLowerCase().includes(q) ||
        (i.serial_number || "").toLowerCase().includes(q) ||
        (i.location_name || "").toLowerCase().includes(q)
      );
    });
  };

  // ---------- Deployment location search helpers ----------
  const filteredLocations = locations.filter((loc) =>
    loc.location_name.toLowerCase().includes(locationSearch.toLowerCase())
  );

  const handleSelectLocation = (loc) => {
    setForm((prev) => ({ ...prev, deployment_location_id: loc.location_id }));
    setLocationSearch(loc.location_name);
    setShowLocationDropdown(false);
  };

  const handleLocationInputChange = (e) => {
    setLocationSearch(e.target.value);
    setShowLocationDropdown(true);
    if (form.deployment_location_id) {
      setForm((prev) => ({ ...prev, deployment_location_id: "" }));
    }
  };

  // DEPLOY uses full inventory list
  const deployListBase = inventory;

  // COLLECT uses deployment-location inventory; optionally includes PO
  const collectListBase = locationInventory.filter((i) =>
    collectIncludePO
      ? i.inventory_type === "FOC" || i.inventory_type === "PO"
      : i.inventory_type === "FOC"
  );

  // TRANSFER uses deployment-location inventory, PO only
  const transferListBase = locationInventory.filter(
    (i) => i.inventory_type === "PO"
  );

  const deployList = filterBySearch(deployListBase, deploySearch);
  const collectList = filterBySearch(collectListBase, collectSearch);
  const transferList = filterBySearch(transferListBase, transferSearch);

  const deploySelectedItems = inventory.filter((i) =>
    deploySelected.includes(i.inventory_id)
  );
  const collectSelectedItems = locationInventory.filter((i) =>
    collectSelected.includes(i.inventory_id)
  );
  const transferSelectedItems = locationInventory.filter((i) =>
    transferSelected.includes(i.inventory_id)
  );

  // When turning OFF "include PO", drop any PO items from collectSelected
  const handleCollectIncludePOChange = (e) => {
    const checked = e.target.checked;
    setCollectIncludePO(checked);

    if (!checked) {
      const focIdsAtLocation = locationInventory
        .filter((i) => i.inventory_type === "FOC")
        .map((i) => i.inventory_id);

      setCollectSelected((prev) =>
        prev.filter((id) => focIdsAtLocation.includes(id))
      );
    }
  };

  // ---------- Step navigation ----------
  const goNext = () => {
    setError("");
    if (step === 1) {
      if (!form.deploymenttype_id) {
        return setError("Please select a deployment type before continuing.");
      }
      if (!form.deployment_location_id) {
        return setError("Please select a deployment location before continuing.");
      }
    }
    if (step < 4) setStep((s) => s + 1);
  };

  const goBack = () => {
    setError("");
    if (step > 1) setStep((s) => s - 1);
  };

  // ---------- Submit ----------
  const handleSubmit = async () => {
    setError("");

    if (!form.deploymenttype_id) {
      return setError("Deployment type is required.");
    }
    if (!form.deployment_location_id) {
      return setError("Deployment location is required.");
    }
    if (transferSelected.length && !form.transfer_location_id) {
      return setError("Transfer location is required for PO transfers.");
    }

    const payload = {
      deploymenttype_id: Number(form.deploymenttype_id),
      vendor_id: form.vendor_id ? Number(form.vendor_id) : null,
      deployment_location_id: Number(form.deployment_location_id),
      collection_to_location_id: DEFAULT_COLLECTION_LOCATION_ID,
      transfer_location_id: form.transfer_location_id
        ? Number(form.transfer_location_id)
        : null,
      need_approval: form.need_approval,
      deploy_items: deploySelected,
      collect_items: collectSelected,
      transfer_items: transferSelected,
      created_by: userId,
    };

    setSubmitting(true);
    try {
      const res = await fetch("/api/deployments", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Failed to create deployment.");

      onCreated?.();
    } catch (err) {
      console.error("Create deployment error", err);
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFinalSubmit = (e) => {
    e.preventDefault();
    if (!submitting) {
      handleSubmit();
    }
  };

  // ---------- Render helpers ----------
  const renderInventoryTable = ({
    list,
    selectedIds,
    onToggle,
    searchValue,
    onSearchChange,
    showType = false,
  }) => (
    <>
      <div className="deployment-table-search">
        <label>Search Inventory:</label>
        <input
          className="deployment-search-input"
          type="text"
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search by model / IMEI / Serial / Location"
        />
      </div>

      <div className="deployment-selection-table-wrapper">
        <table className="deployment-table">
          <thead>
            <tr>
              <th />
              <th>Model</th>
              <th>Colour</th>
              <th>IMEI</th>
              <th>Serial</th>
              {showType && <th>Type</th>}
              <th>Location</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr>
                <td colSpan={showType ? 6 : 5} style={{ textAlign: "center" }}>
                  No inventory items found.
                </td>
              </tr>
            ) : (
              list.map((item) => (
                <tr key={item.inventory_id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(item.inventory_id)}
                      onChange={() => onToggle(item.inventory_id)}
                    />
                  </td>
                  <td>{item.asset1_name || item.asset1_id}</td>
                  <td>{item.asset2_name || item.asset2_id}</td>
                  <td>{item.imei_number || "-"}</td>
                  <td>{item.serial_number}</td>
                  {showType && <td>{item.inventory_type}</td>}
                  <td>{item.location_name || "-"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );

  const renderSelectedList = (items, label) => (
    <div className="deployment-selected-panel">
      <div className="deployment-selected-header">
        <strong>{label}</strong>
        <span>{items.length} selected</span>
      </div>
      {items.length === 0 ? (
        <p className="deployment-selected-empty">No devices selected.</p>
      ) : (
        <div className="deployment-selected-table-wrapper">
          <table className="deployment-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Model</th>
                <th>IMEI</th>
                <th>Serial</th>
                <th>Type</th>
                <th>Location</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr key={it.inventory_id}>
                  <td>{idx + 1}</td>
                  <td>{it.asset1_name || it.asset1_id}</td>
                  <td>{it.imei_number || "-"}</td>
                  <td>{it.serial_number}</td>
                  <td>{it.inventory_type}</td>
                  <td>{it.location_name || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // ---------- UI ----------
  return (
    <div className="deployment-modal-overlay">
      <div
        className={`deployment-modal ${
          step >= 2 ? "deployment-modal-fixed" : ""
        }`}
      >
        <button className="deployment-close-btn" onClick={onClose}>
          ✖
        </button>

        {/* HEADER */}
        <div className="deployment-header">
          <h3 className="deployment-header-title">New Deployment</h3>
          <div className="deployment-wizard-steps">
            <span className={`deployment-wizard-step ${step === 1 ? "active" : ""}`}>
              INFO
            </span>
            <span className={`deployment-wizard-step ${step === 2 ? "active" : ""}`}>
              DEPLOY
            </span>
            <span className={`deployment-wizard-step ${step === 3 ? "active" : ""}`}>
              COLLECT
            </span>
            <span className={`deployment-wizard-step ${step === 4 ? "active" : ""}`}>
              TRANSFER
            </span>
          </div>
        </div>

        {/* BODY + FOOTER INSIDE FORM */}
        <form onSubmit={(e) => e.preventDefault()} className="deployment-form-wrapper">
          <div className="deployment-body">
            <div className="deployment-scroll-region">
              {error && <p className="deployment-error-text">{error}</p>}

              {/* STEP 1: Info */}
              {step === 1 && (
                <>
                  <div className="deployment-form-group">
                    <label>Deployment Type:</label>
                    <select
                      name="deploymenttype_id"
                      value={form.deploymenttype_id}
                      onChange={updateForm}
                      required
                    >
                      <option value="">Select deployment type</option>
                      {deploymentTypes.map((dt) => (
                        <option
                          key={dt.deploymenttype_id}
                          value={dt.deploymenttype_id}
                        >
                          {dt.deploymenttype_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="deployment-form-group">
                    <label>Vendor:</label>
                    <select
                      name="vendor_id"
                      value={form.vendor_id}
                      onChange={updateForm}
                    >
                      <option value="">Select vendor</option>
                      <option value="1">Spireworks</option>
                      <option value="2">Bleu Sea</option>
                      <option value="3">Wei Creative</option>
                    </select>
                  </div>

                  <div className="deployment-form-group" ref={locationWrapperRef}>
                    <label>Deployment Location:</label>
                    <div className="deployment-location-search-wrapper">
                      <input
                        type="text"
                        className="deployment-search-input"
                        placeholder="Search locations..."
                        value={locationSearch}
                        onChange={handleLocationInputChange}
                        onFocus={() => setShowLocationDropdown(true)}
                        required
                      />
                      {showLocationDropdown && (
                        <div className="deployment-location-dropdown">
                          {filteredLocations.length === 0 ? (
                            <div className="deployment-location-option deployment-location-empty">
                              No locations found.
                            </div>
                          ) : (
                            filteredLocations.map((loc) => (
                              <div
                                key={loc.location_id}
                                className="deployment-location-option"
                                onClick={() => handleSelectLocation(loc)}
                              >
                                {loc.location_name}
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="deployment-form-group">
                    <label htmlFor="needApproval">Need Approval:</label>
                    <div className="deployment-checkbox-row">
                      <input
                        type="checkbox"
                        id="needApproval"
                        name="need_approval"
                        checked={form.need_approval}
                        onChange={updateForm}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* STEP 2: Deploy selection */}
              {step === 2 && (
                <>
                  <h4 className="deployment-step-title">
                    Deploy Devices to Selected Location
                  </h4>

                  {renderInventoryTable({
                    list: deployList,
                    selectedIds: deploySelected,
                    onToggle: toggleDeploy,
                    searchValue: deploySearch,
                    onSearchChange: setDeploySearch,
                    showType: true,
                  })}

                  {renderSelectedList(
                    deploySelectedItems,
                    "Devices to Deploy"
                  )}
                </>
              )}

              {/* STEP 3: Collect */}
              {step === 3 && (
                <>
                  <h4 className="deployment-step-title">
                    Collect FOC Devices
                  </h4>

                  <div className="deployment-form-group include-po-row">
                    <div className="deployment-checkbox-row">
                      <input
                        type="checkbox"
                        id="collectIncludePO"
                        checked={collectIncludePO}
                        onChange={handleCollectIncludePOChange}
                      />
                      <span>
                        Include PO devices (special cases to return to warehouse)
                      </span>
                    </div>
                  </div>

                  {renderInventoryTable({
                    list: collectList,
                    selectedIds: collectSelected,
                    onToggle: toggleCollect,
                    searchValue: collectSearch,
                    onSearchChange: setCollectSearch,
                    showType: true,
                  })}

                  {renderSelectedList(
                    collectSelectedItems,
                    "Devices to Collect"
                  )}
                </>
              )}

              {/* STEP 4: Transfer */}
              {step === 4 && (
                <div className="deployment-step-container">
                  <h4 className="deployment-step-title">Transfer PO Devices</h4>
                  <div className="deployment-form-group">
                    <label>Transfer To Location:</label>
                    <select
                      name="transfer_location_id"
                      value={form.transfer_location_id}
                      onChange={updateForm}
                    >
                      <option value="">Select location (optional)</option>
                      {locations.map((loc) => (
                        <option key={loc.location_id} value={loc.location_id}>
                          {loc.location_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="deployment-transfer-content">
                    {renderInventoryTable({
                      list: transferList,
                      selectedIds: transferSelected,
                      onToggle: toggleTransfer,
                      searchValue: transferSearch,
                      onSearchChange: setTransferSearch,
                      showType: true,
                    })}

                    {renderSelectedList(transferSelectedItems, "PO Devices to Transfer")}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* FOOTER BUTTONS */}
          <div className="deployment-footer">
            <button
              type="button"
              className="deployment-btn-secondary"
              onClick={goBack}
              disabled={step === 1}
            >
              Back
            </button>

            {step < 4 ? (
              <button type="button" className="deployment-btn-primary" onClick={goNext}>
                Next
              </button>
            ) : (
              <button
                type="button"
                className="deployment-btn-primary"
                onClick={handleFinalSubmit}
                disabled={submitting}
              >
                {submitting ? "Saving…" : "Create Deployment"}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}